import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  FileText,
  Flag,
  Landmark,
  Scale,
  Sparkles,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  MediaCarousel,
  PodcastCard,
  SectionHead,
  VideoCard,
  foregroundOnSolidAccent,
} from "../shared/categoryDetailShared";

const ListBlock = ({ items, color, icon: Icon = CheckCircle2 }) => (
  <ul className="space-y-2.5">
    {items.map((line) => (
      <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-gray-800 sm:text-[0.9375rem]">
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: `${color}14`, color }}
        >
          <Icon size={14} strokeWidth={2.25} />
        </span>
        <span className="min-w-0 pt-0.5">{line}</span>
      </li>
    ))}
  </ul>
);

/** Overview — Estate planning & inheritance (Act 360, PNDCL 111, probate). */
export const EstatePlanningInheritanceOverviewTab = ({ data, color }) => (
  <div className="space-y-10">
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_24px_64px_-32px_rgba(15,23,42,0.2)] sm:rounded-[1.85rem]"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-50/50 via-white to-slate-50"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-0 top-0 bottom-0 w-[5px]"
        style={{ background: `linear-gradient(180deg, ${color}, ${color}55)` }}
        aria-hidden
      />
      <div className="relative grid lg:grid-cols-12">
        <div className="flex flex-col justify-center px-6 py-10 sm:p-10 lg:col-span-7 lg:pl-11">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="text-[10px] font-black uppercase tracking-[0.32em]" style={{ color }}>
              LEGAL · ESTATE PLANNING &amp; INHERITANCE
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-slate-300 sm:inline" aria-hidden />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/90 px-3 py-1 text-[11px] font-bold text-slate-600">
              <FileText size={13} style={{ color }} aria-hidden />
              {data.funnelTagline}
            </span>
          </div>

          <h2 className="max-w-2xl text-[1.5rem] font-black leading-[1.2] tracking-tight text-slate-950 sm:text-[1.85rem]">
            {data.introduction.headline}
          </h2>
          <p className="mt-4 max-w-prose text-[0.9375rem] leading-[1.8] text-slate-600 sm:text-base">{data.introduction.lead}</p>
          <p className="mt-4 text-sm font-semibold text-slate-700">{data.introduction.mixtureLead}</p>

          <div className="mt-6 rounded-xl border border-slate-200/80 bg-slate-50/60 p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-wider text-slate-500">Core framework</p>
            <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
              {data.introduction.frameworkBullets.map((f) => (
                <li key={f}>• {f}</li>
              ))}
            </ul>
          </div>

          <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.introduction.risksHeading}</p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {data.introduction.risks.map((r) => (
              <li key={r} className="rounded-lg border border-amber-100 bg-amber-50/45 px-3 py-2 text-sm font-semibold text-amber-950/95">
                • {r}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-2">
            {data.focusAreas.map((tag) => (
              <span
                key={tag}
                className="rounded-lg border border-slate-200/90 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-700"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="relative min-h-[16rem] border-t border-slate-100 lg:col-span-5 lg:border-l lg:border-t-0 lg:min-h-0">
          <img src={data.galleryImages[0]} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/35 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
            <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-md">
              <Landmark size={14} className="text-white" aria-hidden />
              <span className="text-[10px] font-black uppercase tracking-[0.24em] text-white/95">Wills · Probate · Law 111</span>
            </div>
            <p className="mt-3 max-w-sm text-lg font-black leading-snug text-white [text-wrap:balance]">
              Plan early · get grants · protect the home &amp; dependants.
            </p>
          </div>
        </div>
      </div>
    </motion.section>

    <section>
      <SectionHead eyebrow="Foundations" title={data.testateVsIntestate.title} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-emerald-200/70 bg-emerald-50/40 p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider text-emerald-900">{data.testateVsIntestate.testateHeading}</p>
          <p className="mt-3 text-sm font-semibold leading-relaxed text-emerald-950/95">{data.testateVsIntestate.testateBody}</p>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.testateVsIntestate.intestateHeading}</p>
          <p className="mt-3 text-sm font-semibold leading-relaxed text-slate-800">{data.testateVsIntestate.intestateBody}</p>
          <div className="mt-8 rounded-xl border border-slate-100 bg-slate-50 p-5">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {data.testateVsIntestate.whyHeading}
            </p>
            <p className="mt-2 text-sm font-semibold text-slate-800">{data.testateVsIntestate.whyBody}</p>
          </div>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Wills Act 360" title={data.willsSection.title} color={color} />
      <p className="-mt-4 mb-6 max-w-3xl text-sm font-semibold text-slate-600">{data.willsSection.definitionLead}</p>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.willsSection.whyHeading}
          </p>
          <ListBlock items={data.willsSection.whyBullets} color={color} />
          <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.willsSection.coverHeading}</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
            {data.willsSection.coverBullets.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-amber-200/75 bg-amber-50/45 p-6 sm:p-8">
          <p className="flex items-center gap-2 text-sm font-black text-amber-950">
            <AlertTriangle size={17} className="shrink-0 text-amber-600" aria-hidden />
            {data.willsSection.mistakesHeading}
          </p>
          <ul className="mt-3 space-y-1.5 text-sm font-semibold text-amber-950/95">
            {data.willsSection.mistakes.map((m) => (
              <li key={m}>• {m}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Validity" title={data.willValidity.title} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="mb-4 text-sm font-semibold text-slate-800">{data.willValidity.checklistLead}</p>
          <ListBlock items={data.willValidity.checklist} color={color} icon={Scale} />
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-900/[0.08] bg-slate-900/[0.03] p-6">
            <p className="text-sm font-semibold leading-relaxed text-slate-800">{data.willValidity.formsNote}</p>
          </div>
          <div className="rounded-xl border p-6" style={{ borderColor: `${color}40`, background: `${color}0a` }}>
            <p className="text-sm font-black leading-snug text-slate-950">{data.willValidity.practicalMessage}</p>
          </div>
        </div>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Will + executor" title={data.probateSection.title} color={color} />
        <p className="-mt-2 mb-6 text-sm font-semibold text-slate-600">{data.probateSection.intro}</p>
        <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.probateSection.executorPowersHeading}
        </p>
        <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
          {data.probateSection.executorPowers.map((p) => (
            <li key={p}>• {p}</li>
          ))}
        </ul>
        <div className="mt-8 rounded-xl border border-blue-100 bg-blue-50/45 p-5">
          <p className="text-sm font-black text-blue-950">{data.probateSection.problemHeading}</p>
          <p className="mt-2 text-sm font-semibold text-blue-950/95">{data.probateSection.problemBody}</p>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.lettersSection.title}</p>
        <p className="mt-4 text-sm font-semibold leading-relaxed text-slate-700">{data.lettersSection.intro}</p>
        <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.lettersSection.adminDutiesHeading}
        </p>
        <ListBlock items={data.lettersSection.adminDuties} color={color} />
        <div className="mt-8 rounded-xl border border-slate-100 bg-slate-50 p-5">
          <p className="text-sm font-black text-slate-900">{data.lettersSection.withoutHeading}</p>
          <p className="mt-2 text-sm font-semibold text-slate-700">{data.lettersSection.withoutBody}</p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Intestacy" title={data.intestacyDetail.title} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-sm font-semibold text-slate-800">{data.intestacyDetail.bodyLead}</p>
          <div className="mt-8 rounded-xl border border-emerald-200/75 bg-emerald-50/40 p-5">
            <p className="text-xs font-black uppercase tracking-wider text-emerald-950">{data.intestacyDetail.householdRuleLead}</p>
            <p className="mt-2 text-sm font-semibold text-emerald-950/95">{data.intestacyDetail.householdRuleBody}</p>
          </div>
        </div>
        <div className="rounded-xl border border-amber-200/70 bg-amber-50/35 p-6 sm:p-8">
          <p className="text-sm font-black text-amber-950">{data.intestacyDetail.confusionHeading}</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-amber-950/95">
            {data.intestacyDetail.confusionBullets.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
          <p className="mt-6 text-sm font-semibold text-amber-950/90">{data.intestacyDetail.interactionNote}</p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Who gets what" title={data.distributionGuide.title} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.distributionGuide.assetsHeading}</p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {data.distributionGuide.assets.map((a) => (
              <li key={a} className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
                • {a}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.distributionGuide.driversHeading}
          </p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-700">
            {data.distributionGuide.drivers.map((d) => (
              <li key={d}>• {d}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.distributionGuide.stepsHeading}</p>
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm font-semibold text-slate-900">
            {data.distributionGuide.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          <div className="mt-8 rounded-xl border border-red-200/65 bg-red-50/35 p-5">
            <p className="text-sm font-bold text-red-950">{data.distributionGuide.caution}</p>
          </div>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Conflicts" title={data.disputesSection.title} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.disputesSection.causesHeading}</p>
          <ul className="mt-3 space-y-1 text-sm font-semibold text-slate-800">
            {data.disputesSection.causes.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
          <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.disputesSection.questionsHeading}
          </p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
            {data.disputesSection.questions.map((q) => (
              <li key={q}>• {q}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-900/[0.08] bg-slate-950 p-6 text-white sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider text-white/50">{data.disputesSection.actionHeading}</p>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-white/85">
            {data.disputesSection.actions.map((a) => (
              <li key={a}>• {a}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Courts" title={data.courtRole.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="max-w-prose text-sm font-semibold leading-relaxed text-slate-800">{data.courtRole.body}</p>
        <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.courtRole.triggersHeading}
        </p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {data.courtRole.triggers.map((t) => (
            <li key={t} className="rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-800">
              {t}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Reality" title={data.commonProblemsGhana.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <ul className="grid gap-2 sm:grid-cols-2">
          {data.commonProblemsGhana.items.map((i) => (
            <li
              key={i}
              className="rounded-lg border border-slate-100 bg-slate-50/80 px-4 py-3 text-sm font-semibold text-slate-900"
            >
              • {i}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Playbook" title={data.practicalTips.title} color={color} />
      <div className="grid gap-4 md:grid-cols-3">
        {[data.practicalTips.planning, data.practicalTips.afterDeath, data.practicalTips.disputeTip].map((block) => (
          <div key={block.heading} className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {block.heading}
            </p>
            <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-700">
              {block.items.map((it) => (
                <li key={it}>• {it}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Trust" title={data.redFlags.title} color={color} />
      <div className="rounded-xl border border-red-200/70 bg-red-50/35 p-6 sm:p-8">
        <ul className="space-y-3">
          {data.redFlags.items.map((r) => (
            <li key={r} className="flex gap-3 text-sm font-semibold text-red-950">
              <Flag size={16} className="mt-0.5 shrink-0 text-red-500" aria-hidden />
              {r}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Content cluster" title={data.editorialPlaybook.title} color={color} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[
          { label: "Wills", items: data.editorialPlaybook.willsPages },
          { label: "Probate", items: data.editorialPlaybook.probatePages },
          { label: "Letters", items: data.editorialPlaybook.lettersPages },
          { label: "Distribution", items: data.editorialPlaybook.distributionPages },
          { label: "Disputes", items: data.editorialPlaybook.disputePages },
        ].map((col) => (
          <div key={col.label} className="rounded-xl border border-slate-200/90 bg-white p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{col.label}</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {col.items.map((item) => (
                <li key={item}>• {item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Funnel" title={data.funnelApplication.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.funnelApplication.hooksHeading}</p>
        <ul className="mt-3 space-y-2">
          {data.funnelApplication.hooks.map((h) => (
            <li key={h} className="text-sm font-bold text-slate-900">
              • {h}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {[data.funnelApplication.education, data.funnelApplication.authority, data.funnelApplication.conversion].map((step) => (
          <div key={step.label} className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color }}>
              {step.label}
            </p>
            <p className="mt-3 text-sm font-bold leading-relaxed text-slate-900">{step.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-slate-950 p-8 text-white sm:p-10">
      <div
        className="absolute inset-0 opacity-30 blur-3xl"
        style={{ background: `radial-gradient(circle at 30% 20%, ${color}, transparent 55%)` }}
        aria-hidden
      />
      <div className="relative">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.finalTakeaway.title}
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {data.finalTakeaway.pillars.map((p) => (
            <span key={p} className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-black text-white/95">
              {p}
            </span>
          ))}
        </div>
        <p className="mt-8 text-xs font-black uppercase tracking-wider text-white/50">{data.finalTakeaway.messageLead}</p>
        <ul className="mt-3 space-y-2 text-sm font-semibold text-white/85">
          {data.finalTakeaway.messageMistakes.map((m) => (
            <li key={m}>• {m}</li>
          ))}
        </ul>
        <p className="mt-8 flex items-start gap-2 text-base font-black leading-snug text-white">
          <CheckCircle2 size={22} className="mt-1 shrink-0 opacity-90" style={{ color }} aria-hidden />
          {data.finalTakeaway.safeMessage}
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Summary" title={data.bottomLine.title} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-8 text-center shadow-sm">
        <p className="text-lg font-black text-slate-950">{data.bottomLine.lead}</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
          <p className="rounded-lg border border-emerald-200/80 bg-emerald-50/50 p-4 text-sm font-bold text-emerald-950">{data.bottomLine.smooth}</p>
          <span className="text-sm font-black text-slate-400">{data.bottomLine.and}</span>
          <p className="rounded-lg border border-orange-200/80 bg-orange-50/55 p-4 text-sm font-bold text-orange-950">{data.bottomLine.stressful}</p>
        </div>
        <p className="mt-6 inline-flex flex-wrap items-center justify-center gap-2 text-sm font-semibold text-slate-600">
          <ArrowRight size={16} style={{ color }} aria-hidden />
          {data.bottomLine.closing}
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Frequently Asked" title="Most-Asked Questions" color={color} />
      <div className="space-y-2">
        {data.faqs.map((faq) => (
          <FAQItem key={faq.q} q={faq.q} a={faq.a} color={color} />
        ))}
      </div>
    </section>
  </div>
);

export const EstatePlanningInheritanceArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-10">
    <section>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <SectionHead eyebrow="Content Library" title="Articles &amp; Guides" subtitle={data.funnelTagline} color={color} />
        <span className="mb-8 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {data.articles.length} articles
        </span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section
      className="overflow-hidden rounded-xl p-8 sm:p-10"
      style={{
        background: `linear-gradient(128deg, ${color}12, transparent)`,
        border: `1px solid ${color}35`,
      }}
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.editorialPlaybook.leadCtasHeading}
          </p>
          <h3 className="text-xl font-black text-slate-950 sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-600">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl px-7 py-4 text-sm font-black shadow-md transition hover:scale-[1.02]"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register Free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const EstatePlanningInheritanceMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHead
          eyebrow="Watch &amp; listen"
          title="Videos &amp; podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
          color={color}
        />
        <span className="mt-1 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {total} featured
        </span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="epi-media-videos-heading" className="min-w-0">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Estate planning and inheritance videos"
            items={videos}
            header={<h3 id="epi-media-videos-heading" className="text-lg font-black text-slate-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="epi-media-podcasts-heading" className="min-w-0">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Estate planning podcast episodes"
            items={podcasts}
            header={<h3 id="epi-media-podcasts-heading" className="text-lg font-black text-slate-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
          <p className="mt-4 px-2 text-center text-xs leading-relaxed text-gray-400 sm:text-left sm:text-sm md:mt-6">
            Listen links appear here when Spotify, Apple Podcasts, or other feeds are connected from admin or your CMS.
          </p>
        </section>
      ) : null}

      <div className="overflow-hidden rounded-xl bg-slate-950 text-white shadow-lg sm:rounded-[1.5rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-6 sm:p-8 md:p-10">
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles size={11} style={{ color }} aria-hidden />
              For partners
            </div>
            <h3 className="text-xl font-black leading-snug sm:text-2xl">{data.funnelApplication.conversion.text}</h3>
            <p className="mt-3 text-sm font-bold text-white/85">{data.primaryCtaHeading}</p>
            <Link
              to="/partners/onboard"
              className="mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black"
              style={{ background: color, color: foregroundOnSolidAccent(color) }}
            >
              Become a partner
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
          <div className="relative min-h-[12rem] sm:min-h-[14rem] lg:min-h-0">
            <img src={data.galleryImages[1]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-950/85" />
          </div>
        </div>
      </div>
    </div>
  );
};
